import { AUTH_COMMENT_REGARDING_ALL_SERVICES_ID, SERVICE_IDS, ZERO_GUID } from 'modules/enumerations/enumerationIds';

export const isIdEqual = (a, b) => !!a && !!b && a.toLowerCase() === b.toLowerCase();

// Comment services that also offer the shared 'DSP' Regarding option
const DSP_REGARDING_SERVICE_IDS = [SERVICE_IDS.DESTINATION_RENTER_SERVICE_ID, SERVICE_IDS.GLOBAL_SERVICE_ID];

const compareTitles = (a, b) => (a.title || '').localeCompare(b.title || '');

const isUsa = country => country?.title === 'United States';

/**
 * Sorts country options alphabetically by title with the U.S. pinned to the top. Returns a new array.
 */
export const sortCountriesUsFirst = countries =>
  [...(countries ?? [])].sort((a, b) => {
    const aIsUsa = isUsa(a);
    const bIsUsa = isUsa(b);
    if (aIsUsa !== bIsUsa) return aIsUsa ? -1 : 1;
    return compareTitles(a, b);
  });

/**
 * Builds the Service dropdown options for authorization comments (and anything categorized the same way, such as
 * a task rule's Service Category) from the authCommentService enumeration: entries with enumType 1, alphabetized
 * by title.
 */
export const getAuthCommentServiceOptions = authCommentServices =>
  (authCommentServices ?? []).filter(s => s.enumType === 1).sort(compareTitles);

/**
 * Builds the Regarding dropdown options for the selected comment service from the authCommentRegarding
 * enumeration, alphabetized by title. Each option names its service in additionalField; options marked
 * AUTH_COMMENT_REGARDING_ALL_SERVICES_ID are offered under every service, except 'DSP', which only Destination
 * and Global offer. Returns no options until a service is selected.
 *
 * The options depend on the service, so a form that changes the service should also clear its Regarding value.
 */
export const getAuthCommentRegardingOptions = (authCommentRegardings, serviceId) => {
  if (!serviceId || isIdEqual(serviceId, ZERO_GUID)) {
    return [];
  }

  const offersDsp = DSP_REGARDING_SERVICE_IDS.some(id => isIdEqual(id, serviceId));

  return (authCommentRegardings ?? [])
    .filter(
      option =>
        isIdEqual(option.additionalField, serviceId) ||
        (isIdEqual(option.additionalField, AUTH_COMMENT_REGARDING_ALL_SERVICES_ID) && (offersDsp || option.title !== 'DSP')),
    )
    .sort(compareTitles);
};

export const CURRENCY_LABEL_FORMATS = {
  NAME: 'name', // 'US Dollar'
  CODE: 'code', // 'USD'
  LONG: 'long' // 'US Dollar (USD)'
};

/**
 * Builds the standard currency dropdown options used across the app from the
 * currencies enumeration: active currencies with a non-empty code, USD pinned
 * to the top, the rest alphabetized by currency name.
 *
 * All original enumeration fields (id, code, isActive, ...) are preserved on
 * each option, and currencyName always holds the unmodified currency name, so
 * values saved from these options (the enumeration id) are unchanged.
 *
 * labelFormat controls the option title (the text dropdowns render):
 *  - CURRENCY_LABEL_FORMATS.NAME (default): the currency name, e.g. 'US Dollar'
 *  - CURRENCY_LABEL_FORMATS.CODE: the ISO code, e.g. 'USD'
 *  - CURRENCY_LABEL_FORMATS.LONG: name and code, e.g. 'US Dollar (USD)'
 */
export const getCurrencyOptions = (currencies, labelFormat = CURRENCY_LABEL_FORMATS.NAME) => {
  const active = (currencies ?? []).filter(c => c.isActive && c.code !== '');

  const sorted = [...active].sort((a, b) => {
    if (a.code === 'USD' && b.code !== 'USD') return -1;
    if (b.code === 'USD' && a.code !== 'USD') return 1;
    return `${a.title}`.localeCompare(`${b.title}`, undefined, { sensitivity: 'base' });
  });

  return sorted.map(c => {
    let title = c.title;
    if (labelFormat === CURRENCY_LABEL_FORMATS.CODE) {
      title = c.code;
    } else if (labelFormat === CURRENCY_LABEL_FORMATS.LONG) {
      title = `${c.title} (${c.code})`;
    }
    return { ...c, currencyName: c.title, title };
  });
};
